// Axios Client with Interceptors
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { clearAuthTokens, getAccessToken, getRefreshToken, setAuthTokens } from '@/lib/authToken';
import { store } from '@/store';
import { clearAuth, setAccessToken } from '@/store/slices/auth.slice';

import { API_BASE_URL, API_ENDPOINTS, API_TIMEOUT, MESSAGES, ROUTES } from '@/constants';
import { DEFAULT_LANGUAGE_CODE, SUPPORTED_LANGUAGES } from '@/constants/language.constants';

/** Read the current accept-language value from localStorage (set by LanguageContext). */
const getAcceptLanguageHeader = (): string => {
  if (typeof window === 'undefined') return 'en-US,en;q=0.9';
  const savedCode = localStorage.getItem('app_language') ?? DEFAULT_LANGUAGE_CODE;
  const lang = SUPPORTED_LANGUAGES.find((l) => l.code === savedCode);
  return lang?.acceptLanguage ?? 'en-US,en;q=0.9';
};

// Types
interface RefreshResponse {
  success: boolean;
  data: {
    accessToken: string;
    expiresAt: string;
    firebaseUid: string;
  };
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
};

const axiosClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const url = config.url || '';

    if (config.headers) {
      const skipLanguageHeader =
        url.includes('/bridge/prepare') ||
        url.includes('/bank/prepare') ||
        url.includes('bank/submit');
      if (!skipLanguageHeader) {
        config.headers['accept-language'] = getAcceptLanguageHeader();
      }
    }

    // Identity endpoints that don't need tokens
    const isAuthRequest =
      url.includes('/app-auth/login') ||
      url.includes('/app-auth/refresh') ||
      url.includes('/app-auth/register') ||
      url.includes('/verify-email');

    if (!isAuthRequest) {
      const state = store.getState();
      const token = getAccessToken() || state.auth.accessToken;

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// Response interceptor
axiosClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const url = originalRequest?.url || '';

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't refresh if the request itself was an auth request
      const isAuthRequest = url.includes('/login') || url.includes('/refresh');

      if (isAuthRequest) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(axiosClient(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) throw new Error(MESSAGES.API.NO_ACCESS_TOKEN);

        const response = await axios.post<RefreshResponse>(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
          { refreshToken: refreshToken },
        );

        const newAccessToken = response.data?.data?.accessToken;
        if (!newAccessToken) throw new Error(MESSAGES.API.INVALID_REFRESH_RESPONSE);

        // Sync new token
        setAuthTokens({ accessToken: newAccessToken });
        store.dispatch(setAccessToken(newAccessToken));

        onTokenRefreshed(newAccessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return axiosClient(originalRequest);
      } catch (refreshError) {
        // Full logout on refresh failure

        // 1. Inform backend to kill session (Fire and forget, using base axios to avoid interceptor)
        try {
          const token = getAccessToken();
          if (token) {
            axios.post(
              `${API_BASE_URL}${API_ENDPOINTS.AUTH.LOGOUT}`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );
          }
        } catch (err) {
          // Ignore logout errors during refresh failure
        }

        // 2. Add marker for React to clear Privy on load
        if (typeof window !== 'undefined') {
          localStorage.setItem('force_logout_marker', 'true');
        }

        clearAuthTokens();
        store.dispatch(clearAuth());
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden / Deactivated
    if (error.response?.status === 403) {
      const errorData = error.response.data as any;
      const isDeactivated =
        errorData?.error === 'USER_DEACTIVATED' ||
        errorData?.message?.toLowerCase().includes('deactivated');

      if (isDeactivated) {
        clearAuthTokens();
        store.dispatch(clearAuth());
        if (typeof window !== 'undefined') {
          window.location.href = ROUTES.DEACTIVATED;
        }
        return Promise.reject(error);
      }
    }

    // Handle 502 Bad Gateway
    if (error.response?.status === 502) {
      if (typeof window !== 'undefined' && window.location.pathname !== ROUTES.ERROR_502) {
        window.location.href = ROUTES.ERROR_502;
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default axiosClient;
